import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { validationContext } from 'validx'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { DocumentType } from '@/features/documents/DocumentType'
import {
  type InventoryStore,
  type OverrideInventoryItemParams,
} from '@/features/inventory/InventoryStore'
import { type InventoryItem } from '@/features/inventory/InventoryItem'
import { InventoryItemDialogViewModelBase } from '@/features/inventory/admin/inventory-item-dialog/InventoryItemDialogViewModelBase'
import {
  type WorkspaceSelection,
  type WorkspaceSelectorViewModel,
} from '@/features/inventory/admin/WorkspaceSelectorViewModel'
import { type RefreshInventoryItems } from '@/features/inventory/admin/InventorySearchViewModel'

/**
 * Overrides a global service item for a workspace.
 */
export class OverrideInventoryItemDialogViewModel
  extends InventoryItemDialogViewModelBase
  implements WorkspaceSelection
{
  /**
   * The selected workspace
   */
  @observable accessor workspace: Workspace | undefined = Workspace.none

  /**
   * Validations for the workspace
   */
  readonly workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  constructor(
    private readonly _inventoryStore: InventoryStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _refreshItems: RefreshInventoryItems,
    private readonly _getSelectedWorkspace: () => Workspace | undefined,
    readonly workspaceSelectorVm: WorkspaceSelectorViewModel,
    getDocumentTypes: () => DocumentType[],
  ) {
    super(getDocumentTypes)
  }

  /**
   * Whether the workspace is picked in the dialog, rather than being the one
   * the inventory is filtered by.
   */
  @computed
  get canSelectWorkspace() {
    return !this._getSelectedWorkspace()
  }

  /**
   * Set the workspace for the new item
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
  }

  @action.bound
  open(item: InventoryItem) {
    this.workspace = this._getSelectedWorkspace()
    super.open(item)
  }

  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid
    ) {
      return
    }

    if (!this.item || !this.workspace) {
      return
    }

    const overriddenItem: OverrideInventoryItemParams = {
      name: this.item.name,
      description: this.item.description,
      courtesyAmount: this.courtesyQuantity,
      pricing: this.pricingVm.toValues(),
      workspace: this.workspace.id,
      rootId: this.item.id,
      supportingItems: this.supportingItems,
      hideFromPro: this.hideFromPro,
      typeSpecification: this.getInventoryItemTypeSpecification(),
    }

    const workspace = this.workspace
    await this._inventoryStore
      .overrideInventoryItem(overriddenItem)
      .catch(this._createAlertErrorHandler('Overriding the item failed'))
    await this._refreshItems(workspace)

    this._newAlert()
      .withState('success')
      .saying('Success', 'Item Overridden.')
      .autoDismiss()
      .show()

    this.modal.close()
  })
}
